import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, Lightbulb, Award, MessageCircle, Calendar } from 'lucide-react';
import { supabase } from '../supabaseClient';

const NotificationCenter = ({ userId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  // Fermer le dropdown si clic en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Charger les notifications
  useEffect(() => {
    if (userId) {
      loadNotifications();
      generateDailyNotifications();
    }
  }, [userId]);

  const loadNotifications = async () => {
    // En attendant une vraie table, on utilise localStorage
    const stored = localStorage.getItem(`notifs_${userId}`);
    if (stored) {
      const notifs = JSON.parse(stored);
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    }
  };

  const saveNotifications = (notifs) => {
    localStorage.setItem(`notifs_${userId}`, JSON.stringify(notifs));
    setNotifications(notifs);
    setUnreadCount(notifs.filter(n => !n.read).length);
  };

  const generateDailyNotifications = () => {
    const today = new Date().toDateString();
    const lastGen = localStorage.getItem(`notifs_lastgen_${userId}`);
    
    if (lastGen === today) return;

    const tips = [
      { type: 'tip', icon: '💡', title: 'Conseil du jour', text: 'Buvez un grand verre d\'eau avant chaque repas pour mieux contrôler votre appétit.' },
      { type: 'tip', icon: '🥗', title: 'Astuce nutrition', text: 'Ajoutez une portion de légumes à chaque repas, même au petit-déjeuner !' },
      { type: 'tip', icon: '⏰', title: 'Rappel', text: 'Manger lentement aide à mieux digérer et à se sentir rassasié plus vite.' },
      { type: 'tip', icon: '🍎', title: 'Le saviez-vous ?', text: 'Une pomme contient autant de caféine naturelle qu\'un café... essayez !' },
      { type: 'tip', icon: '💪', title: 'Motivation', text: 'Chaque bon choix alimentaire est une victoire. Continuez !' },
      { type: 'tip', icon: '🥜', title: 'Conseil snack', text: 'Une poignée de noix est le coupe-faim parfait pour l\'après-midi.' },
      { type: 'tip', icon: '🌙', title: 'Astuce du soir', text: 'Évitez les écrans pendant le repas pour mieux apprécier ce que vous mangez.' },
    ];

    const randomTip = tips[Math.floor(Math.random() * tips.length)];
    
    const newNotif = {
      id: Date.now(),
      ...randomTip,
      time: new Date().toISOString(),
      read: false
    };

    const stored = localStorage.getItem(`notifs_${userId}`);
    const existing = stored ? JSON.parse(stored) : [];
    
    // Garder max 20 notifications
    const updated = [newNotif, ...existing].slice(0, 20);
    saveNotifications(updated);
    
    localStorage.setItem(`notifs_lastgen_${userId}`, today);
  };

  const markAsRead = (notifId) => {
    const updated = notifications.map(n => 
      n.id === notifId ? { ...n, read: true } : n
    );
    saveNotifications(updated);
  };

  const markAllAsRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    saveNotifications(updated);
  };

  const deleteNotification = (notifId, e) => {
    e.stopPropagation();
    const updated = notifications.filter(n => n.id !== notifId);
    saveNotifications(updated);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays === 1) return 'Hier';
    return `Il y a ${diffDays} jours`;
  };

  const getIcon = (type) => {
    switch(type) {
      case 'tip': return <Lightbulb size={18} />;
      case 'achievement': return <Award size={18} />;
      case 'message': return <MessageCircle size={18} />;
      case 'reminder': return <Calendar size={18} />;
      default: return <Bell size={18} />;
    }
  };

  return (
    <div className="notification-center" ref={dropdownRef}>
      <button 
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <Bell size={22} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead}>Tout marquer lu</button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={32} />
                <p>Aucune notification</p>
              </div>
            ) : (
              notifications.map(notif => (
                <div 
                  key={notif.id}
                  className={`notification-item ${!notif.read ? 'unread' : ''}`}
                  onClick={() => markAsRead(notif.id)}
                >
                  <div className={`notification-icon ${notif.type}`}>
                    {notif.icon || getIcon(notif.type)}
                  </div>
                  <div className="notification-content">
                    <span className="notification-title">{notif.title}</span>
                    <span className="notification-text">{notif.text}</span>
                    <span className="notification-time">{getTimeAgo(notif.time)}</span>
                  </div>
                  <button 
                    className="notification-delete"
                    onClick={(e) => deleteNotification(notif.id, e)}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Fonction utilitaire pour envoyer une notification push web
export const sendPushNotification = async (title, body, icon = '/robot-icon.svg') => {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const registration = await navigator.serviceWorker.ready;
      registration.showNotification(title, {
        body,
        icon,
        badge: '/robot-icon.svg',
        vibrate: [200, 100, 200],
        tag: 'moncoach-notification',
        renotify: true
      });
    } catch (e) {
      // Fallback si pas de service worker
      new Notification(title, { body, icon });
    }
  }
};

// Fonction pour ajouter une notification in-app
export const addInAppNotification = (userId, notification) => {
  const stored = localStorage.getItem(`notifs_${userId}`);
  const existing = stored ? JSON.parse(stored) : [];
  
  const newNotif = {
    id: Date.now(),
    time: new Date().toISOString(),
    read: false,
    ...notification
  };
  
  const updated = [newNotif, ...existing].slice(0, 20);
  localStorage.setItem(`notifs_${userId}`, JSON.stringify(updated));
  
  // Dispatch event pour mettre à jour le composant
  window.dispatchEvent(new CustomEvent('notification-added'));
};

export default NotificationCenter;
